import type { ReactNode } from 'react'
import { Link, useRouteLoaderData } from 'react-router'
import type { RootLoaderType } from '#app/root.tsx'
import { ThemeSwitch } from '#app/routes/resources+/theme-switch.tsx'
import { GtmClaudeBadge, gtmClaudeStripeGradient } from './GtmClaudeBadge.tsx'
import { Logo } from './Logo.tsx'

const brandStripeGradient = 'linear-gradient(180deg, #009e85 0%, #c8ff00 100%)'

interface AuthPageLayoutProps {
	title: string
	description?: string
	children: ReactNode
	footerText?: string
	footerLinkText?: string
	footerLinkTo?: string
	accent?: 'brand' | 'gtmclaude'
}

export function AuthPageLayout({
	title,
	description,
	children,
	footerText,
	footerLinkText,
	footerLinkTo,
	accent = 'brand',
}: AuthPageLayoutProps) {
	const data = useRouteLoaderData<RootLoaderType>('root')
	const stripeBackground =
		accent === 'gtmclaude' ? gtmClaudeStripeGradient : brandStripeGradient
	const isGtm = accent === 'gtmclaude'

	return (
		<div
			className={`relative flex min-h-screen flex-col items-center justify-center bg-background px-4 py-8${isGtm ? ' mcp-theme' : ''}`}
		>
			<div className="absolute right-4 top-4 z-20">
				<ThemeSwitch userPreference={data?.requestInfo.userPrefs.theme} />
			</div>

			<div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
				<div
					className="h-full w-[58px]"
					style={{ background: stripeBackground }}
				/>
			</div>

			<div className="relative z-10 w-full max-w-[428px]">
				<div className="rounded-xl bg-background-composer-support p-1 pb-1 pt-6 shadow-[0px_4px_40px_0px_rgba(11,11,11,0.12)] dark:shadow-[0px_4px_40px_0px_rgba(11,11,11,0.24)]">
					<div className="flex flex-col gap-8 rounded-lg bg-background-composer p-10">
						{isGtm ? (
							<div className="flex flex-col gap-5">
								<div className="flex items-center gap-3">
									<Logo size="small" symbolOnly />
									<span className="text-lg text-muted-foreground/30">×</span>
									<GtmClaudeBadge size="medium" />
								</div>
								<div className="flex flex-col gap-1">
									<h1 className="font-outfit text-2xl font-bold text-heading">
										{title}
									</h1>
									{description && (
										<p className="text-sm leading-relaxed text-foreground-muted">
											{description}
										</p>
									)}
								</div>
							</div>
						) : (
							<div className="flex items-start justify-between gap-4">
								<div className="flex min-w-0 flex-col gap-1">
									<h1 className="font-outfit text-[32px] font-bold leading-normal text-heading">
										{title}
									</h1>
									{description && (
										<p className="text-base leading-6 text-foreground-muted">
											{description}
										</p>
									)}
								</div>
								<Logo size="small" symbolOnly />
							</div>
						)}

						<div className="flex flex-col gap-8">{children}</div>

						{footerText && footerLinkText && footerLinkTo && (
							<>
								<div className="h-px w-full bg-border" />
								<div className="flex items-center justify-center gap-4">
									<span className="text-sm text-foreground-muted">
										{footerText}
									</span>
									<Link
										to={footerLinkTo}
										className="text-sm font-medium text-foreground hover:text-foreground/80"
									>
										{footerLinkText}
									</Link>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
